import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import systemService from './service'
import initialApproaches from '../../database/approaches.json'
import initialIssues from '../../database/issues.json'
import initialOrganizationTypes from '../../database/organization-types.json'
import initialAudiences from '../../database/audiences.json'
import { SPECIAL_FIELD_ADAPTER } from '../../constants'

const languageIndex = {
  id: 0,
  en: 1,
}

const languages = [
  {
    id: languageIndex['id'],
    name: 'id'
  },
  {
    id: languageIndex['en'],
    name: 'en'
  }
]

const initialState = {
  _payload: null,
  language: languages[0],
  status: 'idle',
  error: '',
  data: [],
  token: null,
  converterTerms: {
    queryToStateKey: {
      approach: 'approachesById',
      issue: 'issuesById',
      type: 'organizationTypesById',
      audience: 'audiencesById',
      typeApproach: 'approachesById',
      typeIssue: 'issuesById',
      typeOrganisasi: 'organizationTypesById',
      typeAudience: 'audiencesById',
      typeResource: 'typeResourcesById'
    }
  },
  approaches: initialApproaches,
  approachesById: {},
  issues: initialIssues,
  issuesById: {},
  organizationTypes: initialOrganizationTypes,
  organizationTypesById: {},
  audiences: initialAudiences,
  audiencesById: {},
  linkTypes: [],
  resourceTypes: [],
  resourceTypesById: {},
  categories: [],
  categories_resource: [],
  categories_blog: [],
  selectedAudience: {
    label: 'SEMUA ORANG',
    value: 'SEMUA ORANG'
  },
  typeResources: [],
  typeResourcesById: {},
  selectedAudienceId: 1,
  lastSelectedAudienceId: 'default',
  submissionStatus: [],
  submissionStatusById: {},
  needRevisionId: 3,
  activityStatus: [],
  activityStatusById: {},
  activityStatusOptions: [],
  genders: [],
  gendersById: {},
  genderOptions: [],
  genderOptionsByLanguage: {},
  disabilities: [],
  disabilitiesById: [],
  disabilityOptions: [],
  channels: [],
  channelsById: {},
  channelOptions: [],
}

const adapter = (name, payload) => {
  let result = []
  let key = undefined
  let rawdata = []

  if(SPECIAL_FIELD_ADAPTER[name]){
    key = SPECIAL_FIELD_ADAPTER[name]
  }

  if(key){
    if(payload[key] && payload[key].length > 0){
      rawdata = payload[key]
    }
  }

  if(rawdata.length > 0){
    for (let index = 0; index < rawdata.length; index++) {
      const data = rawdata[index];
      result.push({
        id: data.id,
        name: {
          id: data.nama[languageIndex['id']],
          en: data.nama[languageIndex['en']]
        }
      })
    }
  }

  return result
}

export const fetchSystemList = createAsyncThunk('system/fetchSystemList', async () => {
  const list = await systemService.getAll()
  return list
})

export const systemSlice = createSlice({
  name: 'system',
  initialState,
  reducers: {
    changeLanguage(state, action){
      state.language = languages[languageIndex[action.payload]]
    },
    fallback(state){
      state.status = 'succeeded'
      state.error = ''
    },
    setSelectedAudience(state, action){
      const { payload } = action
      const { id, ...rest } = payload
      state.selectedAudienceId = id
      state.selectedAudience = rest

      if(state.lastSelectedAudienceId !== id){
        state.lastSelectedAudienceId = id
      }
    },
    setSelectedAudienceId(state, action){
      state.selectedAudienceId = action.payload
    },
    setLastSelectedAudienceId(state, action){
      state.lastSelectedAudienceId = action.payload
    },
    setToken(state, action){
      state.token = action.payload
    }
  },
  extraReducers: {
    [fetchSystemList.pending]: (state) => {
      state.status = 'loading'
    },
    [fetchSystemList.fulfilled]: (state, action) => {
      state.status = 'succeeded'
      state.data = action.payload
      state._payload = action.payload
      const { payload } = action

      state.approaches = adapter('approach', payload)
      for (let index = 0; index < state.approaches.length; index++) {
        const approach = state.approaches[index];
        state.approachesById[approach.id] = approach
      }

      state.issues = adapter('issue', payload)
      for (let index = 0; index < state.issues.length; index++) {
        const approach = state.issues[index];
        state.issuesById[approach.id] = approach
      }

      state.organizationTypes = adapter('organizationType', payload)
      for (let index = 0; index < state.organizationTypes.length; index++) {
        const approach = state.organizationTypes[index];
        state.organizationTypesById[approach.id] = approach
      }

      state.audiences = adapter('audience', payload)
      for (let index = 0; index < state.audiences.length; index++) {
        const audience = state.audiences[index];
        state.audiencesById[audience.id] = audience
      }

      state.linkTypes = adapter('link', payload)
      const unsorted = adapter('resourceType', payload)
      const sorted = unsorted.sort((a, b) => b.id - a.id)
      state.resourceTypes = sorted

      for (let index = 0; index < state.resourceTypes.length; index++) {
        const resourceType = state.resourceTypes[index];
        state.resourceTypesById[resourceType.id] = resourceType
      }

      const categories = adapterCategories(payload)
      state.categories = categories
      state.categories_resource = categories.filter(c => c.category === 'resource')
      state.categories_blog = categories.filter(c => c.category === 'blog')

      state.typeResources = adapter('typeResources', payload)
      for (let index = 0; index < state.typeResources.length; index++) {
        const typeResource = state.typeResources[index];
        state.typeResourcesById[typeResource.id] = typeResource
      }

      const submissionStatus = adapter('submission', payload)
      state.submissionStatus = submissionStatus
      const submissionStatusById = {}
      for (let index = 0; index < submissionStatus.length; index++) {
        const status = submissionStatus[index];
        submissionStatusById[status.id] = status
      }
      state.submissionStatusById = submissionStatusById

      const activityStatus = adapter('activityStatus', payload)
      state.activityStatus = activityStatus
      const activityStatusById = {}
      const activityStatusOptions = []
      for (let index = 0; index < activityStatus.length; index++) {
        const status = activityStatus[index];
        activityStatusById[status.id] = status
        activityStatusOptions[index] = {
          label: status.name['en'] ? status.name['en'] : status.name['id'],
          value: status.id
        }
      }
      state.activityStatusById = activityStatusById
      state.activityStatusOptions = activityStatusOptions

      const genders = adapter('gender', payload)
      const gendersById = {}
      const genderOptions = []
      const genderOptionsIndonesia = []
      for (let index = 0; index < genders.length; index++) {
        const gender = genders[index];
        gendersById[gender.id] = gender
        genderOptions[index] = {
          label: gender.name['en'],
          value: gender.id
        }
        genderOptionsIndonesia[index] = {
          label: gender.name['id'],
          value: gender.id
        }
      }
      const genderOptionsByLanguage = {
        en: genderOptions,
        id: genderOptionsIndonesia
      }
      state.genders = genders
      state.gendersById = gendersById
      state.genderOptions = genderOptions
      state.genderOptionsByLanguage = genderOptionsByLanguage

      const disabilities = adapter('disability', payload)
      const disabilitiesById = {}
      const disabilityOptions = []
      for (let index = 0; index < disabilities.length; index++) {
        const dis = disabilities[index];
        disabilitiesById[dis.id] = dis
        disabilityOptions[index] = {
          label: dis.name['en'] ? dis.name['en'] : dis.name['id'],
          value: dis.id
        }
      }
      state.disabilities = disabilities
      state.disabilitiesById = disabilitiesById
      state.disabilityOptions = disabilityOptions

      const channels = adapter('channel', payload)
      const channelsById = {}
      const channelOptions = []
      for (let index = 0; index < channels.length; index++) {
        const dis = channels[index];
        channelsById[dis.id] = dis
        channelOptions[index] = {
          label: dis.name['en'] ? dis.name['en'] : dis.name['id'],
          value: dis.id
        }
      }
      state.channels = channels
      state.channelsById = channelsById
      state.channelOptions = channelOptions
    },
    [fetchSystemList.rejected]: (state, action) => {
      state.status = 'failed'
      state.error = action.error.message
    }
  }
})

export const {
  changeLanguage,
  fallback,
  setSelectedAudience,
  setSelectedAudienceId,
  setLastSelectedAudienceId,
  setToken
} = systemSlice.actions;

export default systemSlice.reducer

// Helpers
function adapterCategories(payload) {
  let result = []
  const key = 'Kategori_Artikel'
  let rawdata

  if(payload[key]){
    if(payload[key] && payload[key].length > 0){
      rawdata = payload[key]

      if(rawdata.length > 0){
        for (let index = 0; index < rawdata.length; index++) {
          const data = rawdata[index];
          const category = data.kategori ? (data.kategori[0] ? data.kategori[0].toLowerCase() : data.kategori) : null

          result.push({
            _data: data,
            id: data.id,
            category,
            name: {
              id: data.nama[languageIndex['id']],
              en: data.nama[languageIndex['en']]
            }
          })
        }
      }
    }
  }

  return result
}
